import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2, Clock, CheckCircle, Upload, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/_authenticated/student/assignments")({
  component: StudentAssignments,
});

type AssignmentWithStatus = {
  id: string;
  title: string;
  description: string;
  deadline: string;
  submission_type: string;
  max_grade: number;
  group_name: string;
  submission: {
    id: string;
    status: string;
    grade: number | null;
    feedback: string | null;
    submission_url: string | null;
    submission_note: string | null;
    submitted_at: string | null;
  } | null;
};

function StudentAssignments() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeAssignment, setActiveAssignment] = useState<AssignmentWithStatus | null>(null);
  const [submissionUrl, setSubmissionUrl] = useState("");
  const [submissionNote, setSubmissionNote] = useState("");

  const { data: assignments = [], isLoading: loading } = useQuery({
    queryKey: ["studentAssignments", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: gmData } = await supabase
        .from("group_members")
        .select("group_id, groups(name)")
        .eq("student_id", user.id);

      const groupIds = (gmData || []).map((gm) => gm.group_id);
      if (!groupIds.length) return [];

      const groupNames = (gmData || []).reduce(
        (acc, gm) => {
          acc[gm.group_id] = (gm.groups as any)?.name;
          return acc;
        },
        {} as Record<string, string>,
      );

      const { data: asmData } = await supabase
        .from("assignments")
        .select("*")
        .in("group_id", groupIds)
        .order("deadline", { ascending: true });

      if (!asmData?.length) return [];

      const assignmentIds = asmData.map((a) => a.id);
      const { data: subData } = await supabase
        .from("assignment_submissions")
        .select("*")
        .eq("student_id", user.id)
        .in("assignment_id", assignmentIds);

      const submissionsByAsm = (subData || []).reduce(
        (acc, sub) => {
          acc[sub.assignment_id] = sub;
          return acc;
        },
        {} as Record<string, any>,
      );

      return asmData.map((a) => ({
        id: a.id,
        title: a.title,
        description: a.description || "",
        deadline: a.deadline || "",
        submission_type: a.submission_type || "link_and_note",
        max_grade: a.max_grade || 100,
        group_name: groupNames[a.group_id] || "Unknown Group",
        submission: submissionsByAsm[a.id] || null,
      }));
    },
    enabled: !!user,
  });

  const submitMutation = useMutation({
    mutationFn: async (payload: any) => {
      const { error } = await supabase
        .from("assignment_submissions")
        .upsert(payload, { onConflict: "assignment_id,student_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تسليم الواجب بنجاح");
      setActiveAssignment(null);
      queryClient.invalidateQueries({ queryKey: ["studentAssignments", user?.id] });
    },
    onError: (err: any) => {
      toast.error(err.message || "حدث خطأ أثناء التسليم");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeAssignment || !user) return;

    submitMutation.mutate({
      assignment_id: activeAssignment.id,
      student_id: user.id,
      submission_url: submissionUrl || null,
      submission_note: submissionNote || null,
      status: "submitted",
      submitted_at: new Date().toISOString(),
    });
  };

  if (loading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const getStatusBadge = (status?: string, grade?: number | null, maxGrade?: number) => {
    if (status === "graded")
      return (
        <Badge className="bg-emerald-500 hover:bg-emerald-600 border-0">
          {grade}/{maxGrade}
        </Badge>
      );
    if (status === "submitted")
      return (
        <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 hover:bg-amber-500/20">
          جاري التقييم
        </Badge>
      );
    if (status === "late") return <Badge variant="destructive">تسليم متأخر</Badge>;
    if (status === "returned") return <Badge variant="destructive">مرفوض</Badge>;
    return <Badge variant="outline">مطلوب تسليمه</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-extrabold flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          واجباتي
        </h1>
        <p className="text-muted-foreground">قم بتسليم الواجبات المطلوبة وتابع تقييمات مدرسيك.</p>
      </div>

      {assignments.length === 0 ? (
        <Card className="rounded-3xl border-dashed">
          <CardContent className="p-12 flex flex-col items-center justify-center text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-bold text-lg mb-1">لا توجد واجبات حالياً</h3>
            <p className="text-muted-foreground text-sm">
              كل المهام مكتملة، استمر في هذا الأداء الرائع!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {assignments.map((asm: AssignmentWithStatus) => {
            const isSubmitted = ["submitted", "graded", "late"].includes(
              asm.submission?.status || "",
            );

            return (
              <Card
                key={asm.id}
                className="rounded-3xl border-0 shadow-soft overflow-hidden flex flex-col"
              >
                <CardHeader className="bg-muted/30 pb-4">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <Badge variant="outline" className="bg-background/50 rounded-full text-xs">
                      {asm.group_name}
                    </Badge>
                    {getStatusBadge(asm.submission?.status, asm.submission?.grade, asm.max_grade)}
                  </div>
                  <CardTitle className="text-lg leading-tight">{asm.title}</CardTitle>
                  <div className="flex items-center text-xs text-muted-foreground mt-2 font-medium">
                    <Clock className="h-3 w-3 me-1" />
                    {asm.deadline
                      ? new Date(asm.deadline).toLocaleString("ar-EG")
                      : "بدون موعد محدد"}
                  </div>
                </CardHeader>
                <CardContent className="p-5 flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {asm.description}
                  </p>

                  {asm.submission?.feedback && (
                    <div className="mt-auto mb-4 p-3 rounded-xl bg-primary/5 border border-primary/10">
                      <div className="text-xs font-bold text-primary mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        ملاحظة المدرس
                      </div>
                      <p className="text-xs">{asm.submission.feedback}</p>
                    </div>
                  )}

                  <div className="mt-auto">
                    {isSubmitted ? (
                      <Button
                        variant="outline"
                        className="w-full rounded-full"
                        onClick={() => {
                          setActiveAssignment(asm);
                          setSubmissionUrl(asm.submission?.submission_url || "");
                          setSubmissionNote(asm.submission?.submission_note || "");
                        }}
                      >
                        عرض التسليم
                      </Button>
                    ) : (
                      <Button
                        className="w-full rounded-full bg-gradient-primary text-primary-foreground border-0"
                        onClick={() => {
                          setActiveAssignment(asm);
                          setSubmissionUrl("");
                          setSubmissionNote("");
                        }}
                      >
                        <Upload className="h-4 w-4 me-2" />
                        تسليم الواجب
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!activeAssignment} onOpenChange={(open) => !open && setActiveAssignment(null)}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>{activeAssignment?.title}</DialogTitle>
          </DialogHeader>
          {activeAssignment && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-muted/30 text-sm">
                <p className="font-semibold mb-1">وصف الواجب:</p>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {activeAssignment.description}
                </p>
              </div>

              {activeAssignment.submission?.status === "graded" ? (
                <div className="space-y-4 pt-2 border-t">
                  <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                    <span className="font-bold text-emerald-800">الدرجة:</span>
                    <span className="text-2xl font-black text-emerald-600">
                      {activeAssignment.submission.grade}{" "}
                      <span className="text-sm font-normal text-emerald-600/70">
                        / {activeAssignment.max_grade}
                      </span>
                    </span>
                  </div>
                  {activeAssignment.submission.submission_url && (
                    <div>
                      <Label className="text-muted-foreground">رابط التسليم الخاص بك:</Label>
                      <div className="mt-1">
                        <a
                          href={activeAssignment.submission.submission_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline break-all"
                        >
                          {activeAssignment.submission.submission_url}
                        </a>
                      </div>
                    </div>
                  )}
                  {activeAssignment.submission.submission_note && (
                    <div>
                      <Label className="text-muted-foreground">ملاحظتك:</Label>
                      <div className="mt-1 p-3 bg-muted rounded-xl text-sm">
                        {activeAssignment.submission.submission_note}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {["link_only", "link_and_note"].includes(activeAssignment.submission_type) && (
                    <div className="space-y-2">
                      <Label htmlFor="submission_url">
                        رابط الواجب (مثلاً: رابط مشروع Scratch أو رابط Drive)
                      </Label>
                      <Input
                        id="submission_url"
                        type="url"
                        placeholder="https://..."
                        value={submissionUrl}
                        onChange={(e) => setSubmissionUrl(e.target.value)}
                        required={activeAssignment.submission_type === "link_only"}
                        disabled={
                          !!activeAssignment.submission?.status &&
                          activeAssignment.submission.status !== "returned"
                        }
                      />
                    </div>
                  )}

                  {["note_only", "link_and_note"].includes(activeAssignment.submission_type) && (
                    <div className="space-y-2">
                      <Label htmlFor="submission_note">ملاحظات إضافية (اختياري)</Label>
                      <Textarea
                        id="submission_note"
                        placeholder="اكتب أي ملاحظة للمدرس هنا..."
                        rows={3}
                        value={submissionNote}
                        onChange={(e) => setSubmissionNote(e.target.value)}
                        required={activeAssignment.submission_type === "note_only"}
                        disabled={
                          !!activeAssignment.submission?.status &&
                          activeAssignment.submission.status !== "returned"
                        }
                      />
                    </div>
                  )}

                  {(!activeAssignment.submission?.status ||
                    activeAssignment.submission.status === "returned") && (
                    <Button
                      type="submit"
                      disabled={submitMutation.isPending}
                      className="w-full rounded-full bg-gradient-primary text-primary-foreground border-0"
                    >
                      {submitMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "إرسال الواجب"
                      )}
                    </Button>
                  )}
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
